import { ISubmitFormResult, TFormValidation } from '@/shared/models/formSubmit';

/** A refusal with no readable text; `getServerErrorText` turns it into a sentence. */
export const UNKNOWN_SERVER_ERROR = 'unknown_server_error';

const BASE_FIELD = 'base';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toMessages = (value: unknown): string[] => {
  const list = Array.isArray(value) ? value : [value];

  return list.filter((item): item is string => typeof item === 'string' && item.length > 0);
};

/**
 * Field → messages from either shape a step answers with: `{ field: [messages] }`,
 * or the model-validation list `[{ attribute, type }]` where `type` is a code
 * such as `taken`.
 */
const readStepErrors = (raw: unknown): Array<[string, string[]]> => {
  if (Array.isArray(raw)) {
    return raw.map(entry => {
      if (!isRecord(entry)) {
        return [BASE_FIELD, toMessages(entry)];
      }

      const field = typeof entry.attribute === 'string' ? entry.attribute : BASE_FIELD;

      return [field, toMessages(entry.type ?? entry.raw_type)];
    });
  }

  if (isRecord(raw)) {
    return Object.entries(raw).map(([field, messages]) => [field, toMessages(messages)]);
  }

  return [[BASE_FIELD, toMessages(raw)]];
};

/**
 * A step's errors split into the ones a form field shows and the rest, which go
 * above the form. `fields` maps server names to form ones. A refusal with no
 * readable message still yields one, so the form never treats it as success.
 */
export const splitStepErrors = <TForm extends Record<string, any>>(
  raw: unknown,
  fields: Record<string, keyof TForm & string>,
): ISubmitFormResult<TForm> => {
  if (raw === undefined || raw === null) {
    return {};
  }

  const validationErrors: Record<string, string[]> = {};
  const baseErrors: string[] = [];

  readStepErrors(raw).forEach(([serverField, messages]) => {
    const formField = fields[serverField];

    if (formField) {
      validationErrors[formField] = [...(validationErrors[formField] ?? []), ...messages];
    } else {
      baseErrors.push(...messages);
    }
  });

  const hasFieldErrors = Object.values(validationErrors).some(messages => messages.length > 0);

  if (!hasFieldErrors && baseErrors.length === 0) {
    baseErrors.push(UNKNOWN_SERVER_ERROR);
  }

  return {
    validationErrors: hasFieldErrors ? (validationErrors as TFormValidation<TForm>) : undefined,
    baseErrors: baseErrors.length > 0 ? baseErrors : undefined,
  };
};

/** Whether the server took the step: no field errors and no general ones. */
export const isStepAccepted = (result: ISubmitFormResult<Record<string, any>>): boolean =>
  !result.validationErrors && !result.baseErrors?.length;

/** What the form shows when the request itself failed (network, 5xx, 429). */
export const failedStepResult = (error: { message?: string }): ISubmitFormResult<any> => ({
  baseErrors: [error.message || UNKNOWN_SERVER_ERROR],
});
